<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <validation-observer ref="refFormObserver">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h5 class="mb-0">{{ $t("dmp.systemReport.title") }}</h5>
            <div class="d-flex gap-1">
              <b-button v-if="!isTemplateMode" variant="primary" size="sm" @click="saveSystemReport" :disabled="isSaving">
                <b-spinner v-if="isSaving" small class="mr-25" />
                <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
                {{ isSaving ? $t("actions.saving") : $t("actions.save") }}
              </b-button>
            </div>
          </div>

          <!-- Loading spinner -->
          <div v-if="isLoading" class="d-flex justify-content-center p-3">
            <b-spinner variant="primary" :label="$t('dmp.systemReport.loading')" />
            <span class="ml-2">{{ $t("dmp.systemReport.loading") }}...</span>
          </div>

          <b-form v-else @submit.prevent="saveSystemReport">
            <b-row>

              <!-- Zone Restorals & Access Keypad Enable -->
              <b-col cols="12">
                <b-card class="mb-2 border">
                  <b-row>
                    <!-- Zone Restorals -->
                    <b-col cols="12" md="6" class="mb-2">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.systemReport.zoneRestorals')"
                        rules="required"
                      >
                        <b-form-group
                          :label="$t('dmp.systemReport.zoneRestorals')"
                          label-for="zone-restorals"
                        >
                          <b-form-select
                            id="zone-restorals"
                            v-model="systemReport.zoneRestorals"
                            :options="zoneRestoralOptions"
                            :state="getValidationState(validationContext)"
                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Access Keypad Enable -->
                    <b-col cols="12" md="6" class="mb-2">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.systemReport.accessKeypadEnable')"
                        rules="required"
                      >
                        <b-form-group
                          :label="$t('dmp.systemReport.accessKeypadEnable')"
                          label-for="access-keypad-enable"
                        >
                          <b-form-input
                            id="access-keypad-enable"
                            v-model="systemReport.accessKeypadEnable"
                            :state="getValidationState(validationContext)"
                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>

              <!-- Report Checkboxes & Numeric Fields -->
              <b-col cols="12">
                <b-card class="mb-2 border">
                  <h6 class="mb-3 font-weight-bold">
                    {{ $t("dmp.systemReport.optionsSection") }}
                  </h6>

                  <b-row>
                    <!-- Abort Reports -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <b-form-checkbox
                        id="abort-reports"
                        v-model="systemReport.abortReports"
                        value="Y"
                        unchecked-value="N"
                      >
                        {{ $t("dmp.systemReport.abortReports") }}
                      </b-form-checkbox>
                    </b-col>

                    <!-- Bypass Reports -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <b-form-checkbox
                        id="bypass-reports"
                        v-model="systemReport.bypassReports"
                        value="Y"
                        unchecked-value="N"
                      >
                        {{ $t("dmp.systemReport.bypassReports") }}
                      </b-form-checkbox>
                    </b-col>

                    <!-- Schedule Change Reports -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <b-form-checkbox
                        id="schedule-change-reports"
                        v-model="systemReport.scheduleChangeReports"
                        value="Y"
                        unchecked-value="N"
                      >
                        {{ $t("dmp.systemReport.scheduleChangeReports") }}
                      </b-form-checkbox>
                    </b-col>

                    <!-- Code Change Reports -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <b-form-checkbox
                        id="code-change-reports"
                        v-model="systemReport.codeChangeReports"
                        value="Y"
                        unchecked-value="N"
                      >
                        {{ $t("dmp.systemReport.codeChangeReports") }}
                      </b-form-checkbox>
                    </b-col>

                    <!-- Ambush Reports -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <b-form-checkbox
                        id="ambush-reports"
                        v-model="systemReport.ambushReports"
                        value="Y"
                        unchecked-value="N"
                      >
                        {{ $t("dmp.systemReport.ambushReports") }}
                      </b-form-checkbox>
                    </b-col>

                    <!-- Video System Reports -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <b-form-checkbox
                        id="video-system-reports"
                        v-model="systemReport.videoSystemReports"
                        value="Y"
                        unchecked-value="N"
                      >
                        {{ $t("dmp.systemReport.videoSystemReports") }}
                      </b-form-checkbox>
                    </b-col>
                  </b-row>

                  <hr class="my-2" />

                  <b-row>
                    <!-- Late To Open -->
                    <b-col cols="12" md="6" class="mb-2">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.systemReport.lateToOpen')"
                        rules="required|integer|min_value:0|max_value:999"
                      >
                        <b-form-group
                          :label="$t('dmp.systemReport.lateToOpen')"
                          label-for="late-to-open"
                        >
                          <b-form-input
                            id="late-to-open"
                            v-model="systemReport.lateToOpen"
                            :state="getValidationState(validationContext)"
                            maxlength="3"
                            placeholder=""
                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Early To Close -->
                    <b-col cols="12" md="6" class="mb-2">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.systemReport.earlyToClose')"
                        rules="required|integer|min_value:0|max_value:999"
                      >
                        <b-form-group
                          :label="$t('dmp.systemReport.earlyToClose')"
                          label-for="early-to-close"
                        >
                          <b-form-input
                            id="early-to-close"
                            v-model="systemReport.earlyToClose"
                            :state="getValidationState(validationContext)"
                            maxlength="3"
                            placeholder=""

                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>

            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, inject, getCurrentInstance, watch } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { useToast } from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { formatToThreeDigits } from "./usePanelConfig";
import { useTemplateMode } from "./useTemplateMode";

export default {
  name: "DmpPanelsConfigSystemReport",
  components: {
    ValidationObserver,
    ValidationProvider,
    ToastificationContent,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    systemReportData: {
      type: Object,
      default: () => ({}),
    },
    panelId: {
      type: [Number, String],
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: "default",
    },
  },
  setup(props, { emit }) {
    const { t } = useI18nUtils();
    const toast = useToast();
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;
    const { isTemplateMode, emitTemplateUpdate } = useTemplateMode(props, emit);

    const { refFormObserver, getValidationState } = formValidation();

    const isSaving = ref(false);
    const isLoading = ref(false);

    const systemReport = ref({
      abortReports:          "N",
      accessKeypadEnable:    "1-16",
      ambushReports:         "N",
      bypassReports:         "Y",
      codeChangeReports:     "Y",
      earlyToClose:          "0",
      lateToOpen:            "0",
      scheduleChangeReports: "Y",
      videoSystemReports:    "N",
      zoneRestorals:         "Y",
    });

    const zoneRestoralOptions = computed(() => [
      { value: "YES", text: "Yes" },
      { value: "NO", text: "No" },
      { value: "DISARM", text: "Disarm" },
    ]);


    const normalizeZoneRestoralsToBack = (value) => {
      const map = {
        Y:    "YES",
        N:     "NO",
        D: "DISARM",
      };
      return map[value] ?? "YES";
    };

    const loadSystemReport = () => {
      if (props.systemReportData && Object.keys(props.systemReportData).length > 0) {
        systemReport.value = {
          abortReports:          props.systemReportData.abortReports          ?? "N",
          accessKeypadEnable:    props.systemReportData.accessKeypadEnable    ?? "1-16",
          ambushReports:         props.systemReportData.ambushReports         ?? "N",
          bypassReports:         props.systemReportData.bypassReports         ?? "Y",
          codeChangeReports:     props.systemReportData.codeChangeReports     ?? "Y",
          earlyToClose:          props.systemReportData.earlyToClose          ?? "0",
          lateToOpen:            props.systemReportData.lateToOpen            ?? "0",
          scheduleChangeReports: props.systemReportData.scheduleChangeReports ?? "Y",
          videoSystemReports:    props.systemReportData.videoSystemReports    ?? "N",
          zoneRestorals:         normalizeZoneRestoralsToBack(props.systemReportData.zoneRestorals),
        };
      }
    };

    const formatLateToOpen = () => {
      systemReport.value.lateToOpen = formatToThreeDigits(
        systemReport.value.lateToOpen,
        0,
        999
      );
    };

    const formatEarlyToClose = () => {
      systemReport.value.earlyToClose = formatToThreeDigits(
        systemReport.value.earlyToClose,
        0,
        999
      );
    };

    // Construye la sección en forma GET para el template (inverso de
    // normalizeZoneRestoralsToBack).
    const buildTemplateItem = () => {
      const toCode = { YES: "Y", NO: "N", DISARM: "D" };
      return {
        ...(props.systemReportData || {}),
        abortReports:          systemReport.value.abortReports,
        accessKeypadEnable:    systemReport.value.accessKeypadEnable,
        ambushReports:         systemReport.value.ambushReports,
        bypassReports:         systemReport.value.bypassReports,
        codeChangeReports:     systemReport.value.codeChangeReports,
        earlyToClose:          systemReport.value.earlyToClose,
        lateToOpen:            systemReport.value.lateToOpen,
        scheduleChangeReports: systemReport.value.scheduleChangeReports,
        videoSystemReports:    systemReport.value.videoSystemReports,
        zoneRestorals:         toCode[systemReport.value.zoneRestorals] ?? systemReport.value.zoneRestorals,
      };
    };

    const validateTemplateSection = async () =>
      refFormObserver.value ? await refFormObserver.value.validate() : true;

    const saveSystemReport = async () => {
      const isValid = await refFormObserver.value.validate();
      if (!isValid) return;

      if (isTemplateMode.value) {
        emitTemplateUpdate("system_report", buildTemplateItem());
        toast({
          component: ToastificationContent,
          props: {
            title:   proxy.$t("dmp.common.success"),
            text:    proxy.$t("dmp.systemReport.savedSuccessfully"),
            icon:    "CheckIcon",
            variant: "success",
          },
        });
        return;
      }

      isSaving.value = true;

      try {
        const payload = {
          abortReports:          systemReport.value.abortReports,
          accessKeypadEnable:    systemReport.value.accessKeypadEnable,
          ambushReports:         systemReport.value.ambushReports,
          bypassReports:         systemReport.value.bypassReports,
          codeChangeReports:     systemReport.value.codeChangeReports,
          earlyToClose:          systemReport.value.earlyToClose,
          lateToOpen:            systemReport.value.lateToOpen,
          scheduleChangeReports: systemReport.value.scheduleChangeReports,
          videoSystemReports:    systemReport.value.videoSystemReports,
          zoneRestorals:         systemReport.value.zoneRestorals,
        };

        await store.dispatch("dmp_panels/setSystemReport", {
          uuid: props.uuid,
          systemReportData: payload,
        });

        toast({
          component: ToastificationContent,
          props: {
            title:   proxy.$t("dmp.common.success"),
            text:    proxy.$t("dmp.systemReport.savedSuccessfully"),
            icon:    "CheckIcon",
            variant: "success",
          },
        });

        emit("operation-completed");
      } catch (error) {
        console.error("Error saving system report:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title:   proxy.$t("dmp.common.error"),
            text:    proxy.$t("dmp.systemReport.errorSaving"),
            icon:    "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        isSaving.value = false;
      }
    };

    watch(
      () => props.systemReportData,
      (newData) => {
        if (newData && Object.keys(newData).length > 0) {
          loadSystemReport();
        }
      },
      { deep: true }
    );

    const registerTemplateSection = inject("registerTemplateSection", null);
    const unregisterTemplateSection = inject("unregisterTemplateSection", null);

    onMounted(() => {
      loadSystemReport();
      if (isTemplateMode.value && registerTemplateSection) {
        registerTemplateSection("system_report", {
          validate: validateTemplateSection,
          collect: buildTemplateItem,
        });
      }
    });

    onBeforeUnmount(() => {
      if (isTemplateMode.value && unregisterTemplateSection) {
        unregisterTemplateSection("system_report");
      }
    });

    return {
      refFormObserver,
      getValidationState,
      isSaving,
      isLoading,
      systemReport,
      zoneRestoralOptions,
      formatLateToOpen,
      formatEarlyToClose,
      saveSystemReport,
      isTemplateMode,
    };
  },
};
</script>

<style lang="scss" scoped>
.border {
  border: 1px solid #ebe9f1 !important;
}

.dark-layout {
  .border {
    border-color: #404656 !important;
  }
}
</style>
